import { XMLState } from "./XMLState.js";
import { XMLDiff } from "./XMLDiff.js";
import { PatchProcessor } from "./PatchProcessor.js";
import { DomSynchronizer } from "./DomSynchronizer.js";
import { XPathWatchProcessor } from "./XPathWatchProcessor.js";

export class VROC {
	constructor() {
		this.components = new Map(); // Хранилище компонентов: { id -> { state, template, container } }
		this.xsltCache = new Map();  // Кеш XSLT-шаблонов
		this.xsltLoadingPromises = new Map(); // Промисы загрузки шаблонов для предотвращения дублирования
		this.watchRerenderTimers = new Map(); // Таймеры для debouncing watch-перерисовки
		this.pendingWatchPatterns = new Map(); // Накопленные паттерны для каждого компонента
		this.diff = new XMLDiff({
			uniqueTags: ['module', 'pages', 'items', 'standart', 'busines', 'bus']
		});   // Инструмент сравнения XML
		this.patchProcessor = new PatchProcessor(this); // Обработчик патчей
		this.domSynchronizer = new DomSynchronizer(this); // Синхронизатор DOM
		this.watchProcessor = new XPathWatchProcessor(this); // Обработчик watch-элементов
	}

	// Сценарий 1: Динамическая загрузка компонента
	async loadComponent(component) {
		//console.log(component);
		let id = component.id;

		// Загрузка данных
		const xmlResponse = await fetch(component.xmlUrl);
		const xmlText = await xmlResponse.text();

		component.container = $(component.node)[0];
		component.domMap = new Map();
		component.state = new XMLState(xmlText);

		this.components.set(id, component);
		
		// Первоначальный рендеринг только если не режим attach
		if (!component.attach) {
			//console.log(component.node);
			await this.renderFragment(id, component.container, new DOMParser().parseFromString(xmlText, "text/xml"), null, true);
		}
		
		return id;
	}

	// Сценарий 2: Привязка к существующему DOM-элементу
	attachComponent(component) {
		let id = component.id;

		component.container = $(component.node)[0];
		component.domMap = new Map();

		if (component.xmlUrl) {
			fetch(component.xmlUrl)
				.then(r => r.text())
				.then(xml => {
					component.state = new XMLState(xml);
				});
		}

		this.components.set(id, component);
		return id;
	}

	// Обновление компонента
	async updateComponent(id, newXml) {
		const component = this.components.get(id);
		if (!component) return;

		const oldXml = component.state.serialize();
		component.state.update(newXml);
		
		const patches = this.diff.calculatePatch(oldXml, newXml);
		this.applyPatch(id, patches);
	}

	async loadVROC(url, tpl, el) {
		const response = await fetch(url);
		const initXml = await response.text();
		
		window.state = new XMLState(initXml);
		window.lastXML = initXml;

		await this.renderFragment(tpl, el, window.state.doc);
		this.buildDomMap(container);
	}

	buildDomMap(root = document) {
	  root.querySelectorAll('[data-xpath]').forEach(el => {
		this.domMap.set(el.dataset.xpath, el);
	  });
	}

	updateDomMap() {
	  this.domMap.clear();
	  this.buildDomMap();
	}

	async renderFragment(id, target, xmlData, patches = null, newNode = false, viewType = null) {
		const component = this.components.get(id);
		if (!component) return;

		const xslt = await this._getTemplate(component.templateId);

		const processor = new XSLTProcessor();
		processor.importStylesheet(xslt);

		// Если указан тип представления, передаем его в XSLT
		if (viewType) {
			processor.setParameter(null, 'view-type', viewType);
		}

		// Добавляем корневой элемент если его нет
		const wrappedXml = xmlData.documentElement.nodeName === 'module'
			? xmlData
			: new DOMParser().parseFromString(
				`<module>${new XMLSerializer().serializeToString(xmlData)}</module>`,
				"text/xml"
			);

		const fragment = processor.transformToFragment(wrappedXml, document);

		// Очищаем временный корневой элемент
		const rootNode = fragment.firstElementChild?.nodeName === 'MODULE'
			? fragment.firstElementChild
			: fragment;

		// ВАЖНО: Добавляем data-diff-marker к HTML элементам ПОСЛЕ рендеринга
		if (patches) {
			this._markChangedElements(rootNode, patches);
		}

		if (newNode)
		{
			const tempWrapper = document.createElement('div');
			tempWrapper.appendChild(fragment);

			 // Полностью заменяем целевую ноду новым содержимым
			const parent = target.parentNode;
			const newContent = tempWrapper.firstChild; // Получаем корневой элемент фрагмента

			// Заменяем целевую ноду новым содержимым
			if (parent && newContent) {
				parent.replaceChild(newContent, target);

				// Обновляем ссылку на контейнер в компоненте
				component.container = newContent;
			}
		}
		else
		{
			target.replaceChildren(...rootNode.childNodes);
		}

		// Построение карты DOM
		this._buildDomMap(id);
	}

	async _getTemplate(templateId) {
		// Если шаблон уже в кэше, возвращаем его
		if (this.xsltCache.has(templateId)) {
			return this.xsltCache.get(templateId);
		}

		// Если шаблон уже загружается, ждем завершения загрузки
		if (this.xsltLoadingPromises.has(templateId)) {
			await this.xsltLoadingPromises.get(templateId);
			return this.xsltCache.get(templateId);
		}

		// Создаем промис загрузки и сохраняем его
		const loadingPromise = this._loadTemplate(templateId);
		this.xsltLoadingPromises.set(templateId, loadingPromise);

		try {
			const template = await loadingPromise;
			this.xsltCache.set(templateId, template);
			return template;
		} finally {
			// Удаляем промис после завершения загрузки
			this.xsltLoadingPromises.delete(templateId);
		}
	}

	async _loadTemplate(templateId) {
		const response = await fetch(`/vroc/${templateId}.xsl`);
		const xsltText = await response.text();
		return new DOMParser().parseFromString(xsltText, "text/xml");
	}

	/**
	 * Маркирует изменённые HTML элементы атрибутом data-diff-marker
	 * @param {HTMLElement} rootNode - Корневой элемент отрендеренного фрагмента
	 * @param {Array} patches - Массив патчей
	 */
	_markChangedElements(rootNode, patches) {
		// Собираем все уникальные XPath из патчей
		const changedXPaths = new Set();
		patches.forEach(patch => {
			if (patch.xpath) {
				changedXPaths.add(patch.xpath);
			}
		});

		console.log(`[VROC] _markChangedElements: помечаем ${changedXPaths.size} уникальных xpath`);

		// Находим все элементы с data-xpath в отрендеренном фрагменте
		const allElements = rootNode.querySelectorAll('[data-xpath]');

		allElements.forEach(el => {
			const elementXPath = el.getAttribute('data-xpath');

			// Проверяем, был ли этот xpath затронут патчами
			// Проверяем как точное совпадение, так и родительские пути
			for (const changedXPath of changedXPaths) {
				if (elementXPath === changedXPath || elementXPath.startsWith(changedXPath + '/') || changedXPath.startsWith(elementXPath + '/')) {
					el.setAttribute('data-diff-marker', 'true');
					console.log(`[VROC] Маркировка элемента: xpath="${elementXPath}", tag="${el.tagName}", class="${el.className}"`);
					break;
				}
			}
		});
	}

	_buildDomMap(id) {
		const component = this.components.get(id);
		if (!component) return;

		component.domMap.clear();

		// Регистрируем обычные элементы с data-xpath
		component.container.querySelectorAll('[data-xpath]').forEach(el => {
			component.domMap.set(el.dataset.xpath, el);
		});

		// Регистрируем watch-элементы только внутри контейнера компонента
		component.container.querySelectorAll('[data-xpath-watch]').forEach(el => {
			const xpath = el.getAttribute('data-xpath-watch');
			this.watchProcessor.registerWatchElement(el, xpath);
		});
	}



	async applyPatch(id, patches) {
		const component = this.components.get(id);
		if (!component) return;

		const { state } = component;

		// Сначала обрабатываем все обычные патчи
		await this._processRegularPatches(id, patches);

		// После завершения обычных патчей, проверяем watch-элементы
		const affectedPatterns = new Set();

		patches.forEach(patch => {
			const patterns = this.watchProcessor.getAffectedWatchPatterns(patch);
			patterns.forEach(pattern => affectedPatterns.add(pattern));
		});

		// Если есть затронутые watch-паттерны, добавляем их в очередь с debouncing
		if (affectedPatterns.size > 0) {
			this._scheduleWatchRerender(id, Array.from(affectedPatterns));
		}

		if (typeof component.onUpdate === 'function') {
			component.onUpdate();
		}
	}

	/**
	 * Планирует перерисовку watch-элементов с debouncing для избежания множественных вызовов
	 * @param {string} id - ID компонента
	 * @param {Array<string>} affectedPatterns - Затронутые xpath паттерны
	 */
	_scheduleWatchRerender(id, affectedPatterns) {

		// Накапливаем все паттерны для данного компонента
		if (!this.pendingWatchPatterns.has(id)) {
			this.pendingWatchPatterns.set(id, new Set());
		}
		const pending = this.pendingWatchPatterns.get(id);
		affectedPatterns.forEach(pattern => pending.add(pattern));

		// Отменяем предыдущий таймер, если он есть
		if (this.watchRerenderTimers.has(id)) {
			clearTimeout(this.watchRerenderTimers.get(id));
		}

		// Устанавливаем новый таймер с debouncing
		const timer = setTimeout(async () => {
			try {
				const allPatterns = Array.from(pending);
				
				// Очищаем накопленные паттерны и таймер
				this.pendingWatchPatterns.delete(id);
				this.watchRerenderTimers.delete(id);

				// Выполняем перерисовку
				await this.watchProcessor.triggerFullRerender(id, allPatterns);
			} catch (error) {
				console.error('[VROC] Error in debounced watch rerender:', error);
			}
		}, 50); // Увеличенная задержка для более эффективного batching множественных патчей

		this.watchRerenderTimers.set(id, timer);
	}

	/**
	 * Обрабатывает обычные патчи (без watch-элементов)
	 * @param {string} id - ID компонента
	 * @param {Array} patches - Массив патчей
	 */
	async _processRegularPatches(id, patches) {
		const component = this.components.get(id);

		console.log(`[VROC] _processRegularPatches: получено ${patches.length} патчей`);
		patches.forEach(patch => console.log(`[VROC]   Патч:`, patch));

		// Получаем актуальное XML состояние (патчи уже применены в component.state)
		const currentXml = new DOMParser().parseFromString(
			component.state.serialize(),
			"text/xml"
		);

		// Создаём временный контейнер для рендеринга
		const tempContainer = document.createElement('div');

		// Рендерим ВЕСЬ компонент заново (без патчей)
		console.log(`[VROC] Рендеринг всего компонента заново`);
		await this.renderFragment(id, tempContainer, currentXml, null, false);

		// Синхронизируем с основным DOM через DomSynchronizer
		await this.domSynchronizer.syncDomChanges(id, tempContainer);
	}









	_evaluateXPath(xpath, contextNode) {
		// Получаем документ из контекстного узла
		const doc = contextNode.ownerDocument || contextNode;

		// Проверяем, что doc - валидный документ
		if (!doc.evaluate) {
			throw new Error("Invalid document context for XPath evaluation");
		}

		const result = doc.evaluate(
			xpath,
			contextNode, // Контекстный узел для поиска
			null,
			XPathResult.ORDERED_NODE_SNAPSHOT_TYPE,
			null
		);

		const nodes = [];
		for (let i = 0; i < result.snapshotLength; i++) {
			nodes.push(result.snapshotItem(i));
		}
		return nodes;
	}

	detachComponent(id) {
		const component = this.components.get(id);
		if (!component) return;

		// Очищаем watch-элементы для этого компонента
		this.watchProcessor.clearWatchElements(id);

		// Очищаем контейнер компонента
		if (component.container) {
			component.container.innerHTML = '';
		}

		// Удаляем компонент из хранилища
		this.components.delete(id);
	}
}
